import React from 'react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

export interface DesktopOnlyModalProps {
	/** Whether the modal is visible */
	visible: boolean
	/** Called when the modal is dismissed (close button or outside click) */
	onClose: () => void
	/** Optional custom title (default: "Functionality not available") */
	title?: string
	/** Optional custom description text */
	description?: string
	/** Optional close button label (default: "Close") */
	closeText?: string
}

/**
 * Reusable modal to indicate that a feature is only
 * available on Desktop.
 *
 * Shared between different features (zones, campaigns, etc.).
 */
export const DesktopOnlyModal: React.FC<DesktopOnlyModalProps> = ({
	visible,
	onClose,
	title,
	description,
	closeText,
}) => {
	const { t } = useTranslation('common')

	const resolvedTitle = title ?? t('common.desktop_only_modal.title')
	const resolvedDescription =
		description ?? t('common.desktop_only_modal.description')
	const resolvedCloseText = closeText ?? t('actions.close')

	return (
		<Dialog
			open={visible}
			onOpenChange={(open) => {
				// Only trigger close callback when dialog is being closed
				if (!open) {
					onClose()
				}
			}}
		>
			<DialogContent className="sm:max-w-[425px]">
				<div className="px-6 py-5 border-b">
					<DialogTitle className="text-lg">{resolvedTitle}</DialogTitle>
				</div>
				<div className="px-6 py-6">
					<p className="text-sm text-muted-foreground">{resolvedDescription}</p>
				</div>
				<div className="px-6 py-5 border-t flex justify-end">
					<Button variant="default" onClick={onClose}>
						{resolvedCloseText}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default DesktopOnlyModal
